/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import Person from "@/src/components/Form/Common/Person/Person";
import Address from "@/src/components/Form/Common/Address/Address";
import { dateFieldDefault, isPhoneNumber, minStringLength, pastOrSameDateDefault, requiredValidator } from "@/src/services/validationRules";
import { useForm } from "@/src/provider/Form/FormProvider";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import ValidatorDateField from "@/src/components/FormFields/ValidatorDateField";
import { isInsurerPartnered } from "@/src/services/productService";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";

type DataLeakContractBaseProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function DataLeakContractBase({ submitProps, product }: DataLeakContractBaseProps) {
  const { handleSubmit } = useForm();

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Vertrag
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Allgemeines zu Ihrem Vertrag</strong>
              </FormLegend>
            </Grid>
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>
                {product.powerOfAttorney
                  ? "Vielen Dank für die Beauftragung! Wir benötigen nun nur noch kurz ergänzende Angaben zu Ihrem Mobilfunkvertrag und Ihrer Versicherung, damit wir tätig werden können."
                  : "Vielen Dank! Wir benötigen nun nur noch ein paar ergänzende Angaben zu Ihrem Mobilfunkvertrag, Ihrer Versicherung und Ihre Vollmacht, damit wir tätig werden können. Sobald diese Daten vorliegen, prüfen wir Ihren Anspruch auf Schadensersatz!"}
                {!isInsurerPartnered(product) &&
                  " Es erfolgt zunächst keine Meldung bei Ihrer Versicherung. Erst wenn die Prüfung ergibt, dass Schadensersatz geltend gemacht werden kann, werden wir gegenüber dem Mobilfunkanbieter tätig und kümmern uns dann auch um die Deckung durch die Versicherung."}
              </FormLegend>
            </Grid>
            <Person path="customer.person" headline={"Wie heißen Sie?"} />
            <Address path="customer.person.residenceAddress" headline={"Wo wohnen Sie?"} />
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>Wie können wir Sie erreichen?</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name="customer.telephone"
                label="Ihre Telefonnummer"
                autoComplete="tel"
                validators={[
                  requiredValidator("Bitte geben Sie Ihre Telefonnummer an"),
                  {
                    validator: minStringLength,
                    params: [7],
                    message: "Ihre Telefonnummer ist zu kurz",
                  },
                  { validator: isPhoneNumber, message: "Ihre Telefonnummer enthält ungültige Zeichen" },
                ]}
              />
            </Grid>
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>
                Wann wurde der Vertrag abgeschlossen? Wenn Sie den Tag nicht genau benennen können, geben Sie einfach ein ungefähres Datum ein, damit wir
                wissen, wie lang der Vertragsschluss her ist.
              </FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorDateField name="contractDate" label="Datum des Vertrags" validators={[dateFieldDefault, pastOrSameDateDefault]} />
            </Grid>
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
